/**
 * InsightPro™ — Layout v7
 * Mobile responsive — sidebar is overlay drawer on mobile, pinnable on desktop
 * v7: Removed CapacityContext + Azure A1 capacity status bar (retired with embedToken model).
 *     StatusBar now shows static service health indicators only.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../utils/firebase';
import { useCompany } from '../context/CompanyContext';

// permKey: gates this item against selectedCompany.permissions[permKey]?.view.
// Items without a permKey are always shown. If the user's membership has no
// permissions object (legacy onboards), all items are shown (no regression).
const NAV = [
  { label: 'Executive Overview', path: '/',         section: 'Intelligence', permKey: 'executiveOverview' },
  { label: 'Sales & Revenue',    path: '/sales',    section: 'Intelligence', permKey: 'sales' },
  { label: 'Stock Optimisation', path: '/stock-optimisation', section: 'Intelligence', permKey: 'stock' },
  { label: 'Stock & Inventory',  path: '/stock',    section: 'Intelligence', permKey: 'stock' },
  { label: 'Debtors',     path: '/debtors',    section: 'Intelligence', permKey: 'debtors' },
  { label: 'Creditors',    path: '/creditors',  section: 'Intelligence', permKey: 'debtors' },
  { label: 'Branch Performance', path: '/branches', section: 'Intelligence', permKey: 'branches' },
  { label: 'AI Advisor',         path: '/advisor',  section: 'Intelligence', permKey: 'advisory' },
  { label: 'Group Consolidated', path: '/',                  section: 'Finance', permKey: 'groupConsol' },
  { label: 'Group P&L',          path: '/group-pl', section: 'Finance',      permKey: 'groupPL' },
  { label: 'Store Performance',  path: '/store-performance', section: 'Finance', permKey: 'storePerformance' },
  { label: 'Balance Sheet',      path: '/balance-sheet',     section: 'Finance', permKey: 'balanceSheet' },
  { label: 'Expense Analysis',   path: '/expense-analysis',  section: 'Finance', permKey: 'expenseAnalysis' },
  { label: 'Cash Flow',          path: '/cash-flow',         section: 'Finance', permKey: 'cashFlow' },
  { label: 'Notes & Commentary', path: '/notes',             section: 'Finance', permKey: 'notes' },
  { label: 'Advisory Notes',     path: '/advisory', section: 'Governance',   permKey: 'advisory' },
  { label: 'Action Tracking',    path: '/actions',  section: 'Governance',   permKey: 'actions' },
  { label: 'Alert Preferences',   path: '/alerts',   section: 'Governance'   },
];

const NAV_ADMIN = [
  { label: 'QA Agent',          path: '/qa',             section: 'System' },
  { label: 'Client Management', path: '/admin/clients',  section: 'Admin'  },
  { label: 'Onboard Client',    path: '/admin/onboard',  section: 'Admin'  },
];

const SIDEBAR_W = 230;
const MOBILE_BP = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BP);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < MOBILE_BP);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return isMobile;
}

export default function Layout({ user, claims }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const isMobile  = useIsMobile();

  const [period,        setPeriod]        = useState('MTD');

  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [pinned,        setPinned]        = useState(true);

  const isSuperAdmin = claims?.role === 'superAdmin';
  const { selectedCompany, setSelectedCompany, clearCompany, clearAllCompanyData } = useCompany();
  const perms        = selectedCompany?.permissions;
  const hasPerms     = perms && typeof perms === 'object' && Object.keys(perms).length > 0;
  const visibleNav   = (!hasPerms || isSuperAdmin)
    ? NAV
    : NAV.filter(n => !n.permKey || perms[n.permKey]?.view === true);
  const navItems     = isSuperAdmin ? [...visibleNav, ...NAV_ADMIN] : visibleNav;
  const sections     = [...new Set(navItems.map(n => n.section))];
  const clientName   = selectedCompany?.companyName || selectedCompany?.name || null;
  const clientLogo   = selectedCompany?.logoUrl || null;
  const initials     = clientName ? clientName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '…';

  // Desktop: pinned = always open. Mobile: overlay drawer only.
  const effectiveOpen = isMobile ? sidebarOpen : (pinned ? true : sidebarOpen);

  // Close drawer on navigation (mobile)
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  const handleLogout = async () => { clearAllCompanyData(); await logout(); navigate('/login'); };

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setSidebarOpen(o => !o);
    } else {
      if (pinned) { setPinned(false); setSidebarOpen(false); }
      else setSidebarOpen(o => !o);
    }
  }, [isMobile, pinned]);

  return (
    <>
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--page)' }}>

        {/* ── MOBILE BACKDROP ──────────────────────────── */}
        {isMobile && effectiveOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 199,
              background: 'rgba(0,0,0,0.65)',
              backdropFilter: 'blur(2px)',
            }}
          />
        )}

        {/* ── SIDEBAR ──────────────────────────────────── */}
        <aside style={isMobile ? {
          // Mobile: slide-in overlay
          position: 'fixed', left: 0, top: 0, bottom: 0,
          width: SIDEBAR_W,
          background: 'var(--void)',
          borderRight: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column',
          zIndex: 200,
          transform: effectiveOpen ? 'translateX(0)' : `translateX(-${SIDEBAR_W}px)`,
          transition: 'transform 0.28s cubic-bezier(.4,0,.2,1)',
          overflowY: 'auto',
        } : {
          // Desktop: push layout
          position: 'fixed', left: 0, top: 0, bottom: 0,
          width: effectiveOpen ? SIDEBAR_W : 0,
          minWidth: effectiveOpen ? SIDEBAR_W : 0,
          background: 'var(--void)',
          borderRight: effectiveOpen ? '1px solid var(--border)' : 'none',
          display: 'flex', flexDirection: 'column',
          zIndex: 100, overflow: 'hidden',
          transition: 'width 0.28s cubic-bezier(.4,0,.2,1), min-width 0.28s cubic-bezier(.4,0,.2,1)',
        }}>

          {/* Logo */}
          <div style={{ padding: '20px 18px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 32, height: 32, flexShrink: 0,
                background: 'linear-gradient(135deg, var(--gold-light), var(--gold))',
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 10, color: 'var(--void)' }}>IP</span>
              </div>
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                  InsightPro<sup style={{ fontSize: 8, color: 'var(--gold)', verticalAlign: 'super', fontFamily: 'var(--font-body)' }}>™</sup>
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, letterSpacing: '0.18em', color: 'var(--text-muted)', textTransform: 'uppercase', marginTop: 3 }}>
                  Retail Intelligence
                </div>
              </div>
              {/* Close ×  on mobile */}
              {isMobile && (
                <button
                  onClick={() => setSidebarOpen(false)}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer', padding: '2px 6px', lineHeight: 1 }}
                >×</button>
              )}
            </div>
          </div>

          {/* Client pill with logo */}
          <div style={{ margin: '12px 14px', padding: '10px 12px', flexShrink: 0, background: 'var(--void-3)', border: '1px solid var(--border)', borderRadius: 4 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, letterSpacing: '0.14em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Active Client</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {clientLogo ? (
                <img src={clientLogo} alt={clientName}
                  style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'contain', background: '#fff', padding: 2, flexShrink: 0 }}
                />
              ) : (
                <div style={{
                  width: 24, height: 24, borderRadius: 4, flexShrink: 0,
                  background: 'linear-gradient(135deg, var(--gold-light), var(--gold))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: 8, fontWeight: 800, color: 'var(--void)' }}>{initials}</span>
                </div>
              )}
              <div style={{ fontSize: 12, fontWeight: 600, color: clientName ? 'var(--text-primary)' : 'var(--text-muted)', lineHeight: 1.2, fontStyle: clientName ? 'normal' : 'italic' }}>
                {clientName || 'Loading...'}
              </div>
            </div>
          </div>

          {/* Nav */}
          <nav style={{ flex: 1, overflowY: 'auto', paddingBottom: 16 }}>
            {sections.map(section => (
              <div key={section}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-muted)', padding: '14px 18px 5px' }}>
                  {section}
                </div>
                {navItems.filter(n => n.section === section).map(item => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    style={({ isActive }) => ({
                      display: 'flex', alignItems: 'center', gap: 9,
                      padding: isMobile ? '13px 18px' : '8px 18px',
                      textDecoration: 'none',
                      fontSize: isMobile ? 14 : 12.5,
                      fontFamily: 'var(--font-body)',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? 'var(--text-primary)' : 'var(--text-sec)',
                      borderLeft: `2px solid ${isActive ? 'var(--gold)' : 'transparent'}`,
                      background: isActive ? 'rgba(200,149,32,0.06)' : 'transparent',
                    })}
                  >
                    <span style={{ width: 4, height: 4, borderRadius: '50%', flexShrink: 0, background: 'currentColor', opacity: 0.6 }} />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green-l)', display: 'inline-block', marginRight: 7, boxShadow: '0 0 6px var(--green-l)' }} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-muted)' }}>All systems operational</span>
            </div>
            {!isMobile && (
              <button
                onClick={() => { setPinned(p => !p); if (!pinned) setSidebarOpen(true); }}
                style={{
                  width: '100%', padding: '5px 0', marginBottom: 6,
                  background: pinned ? 'rgba(200,149,32,0.08)' : 'transparent',
                  border: `1px solid ${pinned ? 'var(--border-gold)' : 'var(--border)'}`,
                  borderRadius: 3, cursor: 'pointer',
                  fontFamily: 'var(--font-mono)', fontSize: 8.5,
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: pinned ? 'var(--gold)' : 'var(--text-muted)',
                  transition: 'all 0.15s',
                }}>
                {pinned ? '📌 Pinned — click to unpin' : '📌 Click to pin'}
              </button>
            )}
            <button
              onClick={() => { setSelectedCompany(null); navigate('/select-company'); }}
              style={{
                width: '100%', padding: isMobile ? '11px 0' : '7px 0',
                background: 'transparent',
                border: '1px solid var(--border-gold, rgba(200,149,32,0.3))',
                borderRadius: 3, color: 'var(--gold)',
                fontFamily: 'var(--font-mono)', fontSize: 9,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                cursor: 'pointer', marginBottom: 6,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(200,149,32,0.06)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >⇄ Switch Company</button>
            <button
              onClick={handleLogout}
              style={{
                width: '100%', padding: isMobile ? '11px 0' : '7px 0',
                background: 'transparent', border: '1px solid var(--border)',
                borderRadius: 3, color: 'var(--text-muted)',
                fontFamily: 'var(--font-mono)', fontSize: 9,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >Sign Out</button>
          </div>
        </aside>

        {/* ── MAIN ─────────────────────────────────────── */}
        <div style={{
          marginLeft: isMobile ? 0 : (effectiveOpen ? SIDEBAR_W : 0),
          flex: 1, display: 'flex', flexDirection: 'column',
          minHeight: '100vh', minWidth: 0,
          transition: isMobile ? 'none' : 'margin-left 0.28s cubic-bezier(.4,0,.2,1)',
        }}>

          {/* Topbar */}
          <header style={{
            background: 'var(--void)',
            borderBottom: '1px solid var(--border)',
            position: 'sticky', top: 0, zIndex: 50,
            flexShrink: 0,
          }}>
            {/* Main topbar row */}
            <div style={{
              height: isMobile ? 52 : 'var(--topbar-h)',
              display: 'flex', alignItems: 'center',
              padding: isMobile ? '0 12px' : '0 20px',
              gap: isMobile ? 8 : 12,
            }}>
              {/* Hamburger */}
              <button
                onClick={toggleSidebar}
                style={{
                  width: isMobile ? 36 : 30, height: isMobile ? 36 : 30, flexShrink: 0,
                  background: 'var(--void-3)', border: '1px solid var(--border)',
                  borderRadius: 4, cursor: 'pointer',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                {[14, 10, 14].map((w, i) => (
                  <span key={i} style={{ display: 'block', width: w, height: 1.5, background: 'var(--text-sec)', borderRadius: 1 }} />
                ))}
              </button>

              {/* Logo in topbar on mobile */}
              {isMobile && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
                  <div style={{ width: 24, height: 24, background: 'linear-gradient(135deg, var(--gold-light), var(--gold))', clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 8, color: 'var(--void)' }}>IP</span>
                  </div>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
                    InsightPro<sup style={{ fontSize: 7, color: 'var(--gold)', fontFamily: 'var(--font-body)' }}>™</sup>
                  </span>
                </div>
              )}

              {/* Period selector — desktop only in topbar row */}
              {!isMobile && <PeriodSelector value={period} onChange={setPeriod} isMobile={false} />}

              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 14 }}>
                {!isMobile && <ClockDisplay />}
                <div style={{
                  width: isMobile ? 32 : 30, height: isMobile ? 32 : 30,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, var(--gold-light), var(--gold))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700, color: 'var(--void)',
                  fontFamily: 'var(--font-body)', flexShrink: 0,
                }}>{initials}</div>
              </div>
            </div>

            {/* Period selector — mobile: full-width second row */}
            {isMobile && (
              <div style={{
                padding: '8px 12px 8px',
                borderTop: '1px solid var(--border)',
                display: 'flex', gap: 6, overflowX: 'auto',
                background: 'var(--void)',
              }}>
                <PeriodSelector value={period} onChange={setPeriod} isMobile={true} />
              </div>
            )}
          </header>

          {/* Page content */}
          <main style={{
            flex: 1,
            padding: isMobile ? '14px 12px' : '24px 28px',
            background: 'var(--page)',
            overflowX: 'hidden',
            minWidth: 0,
          }}>
            <Outlet context={{ period }} />
          </main>

          {!isMobile && <StatusBar />}
        </div>
      </div>
    </>
  );
}

function PeriodSelector({ value, onChange, isMobile }) {
  const periods = isMobile
    ? ['TODAY', 'YEST', 'WTD', 'MTD', 'QTD', 'YTD']
    : ['TODAY', 'YESTERDAY', 'WTD', 'MTD', 'QTD', 'YTD'];

  return (
    <div style={{ display: 'flex', gap: isMobile ? 5 : 5, width: isMobile ? '100%' : 'auto' }}>
      {periods.map(p => {
        const active = value === p;
        return (
          <button key={p} onClick={() => onChange(p)} style={{
            flex: isMobile ? 1 : 'none',
            padding: isMobile ? '7px 4px' : '5px 13px',
            fontFamily: 'var(--font-body)',
            fontSize: isMobile ? 12 : 12,
            letterSpacing: '0.01em',
            fontWeight: active ? 700 : 500,
            cursor: 'pointer',
            borderRadius: 4,
            border: active ? '1px solid var(--border-gold)' : '1px solid var(--border)',
            background: active ? 'var(--gold)' : 'rgba(255,255,255,0.04)',
            color: active ? 'var(--void)' : 'var(--text-sec)',
            transition: 'all 0.15s',
            minHeight: isMobile ? 34 : 28,
            whiteSpace: 'nowrap',
            textAlign: 'center',
          }}>{p}</button>
        );
      })}
    </div>
  );
}

function ClockDisplay() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
      {time.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
      {' · '}
      {time.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
    </div>
  );
}

function StatusBar() {
  const items = [
    { label: 'BigQuery',  dot: 'var(--green-l)' },
    { label: 'Data API',  dot: 'var(--green-l)' },
    { label: 'Power BI',  dot: 'var(--green-l)' },
    { label: 'Firebase',  dot: 'var(--green-l)' },
  ];
  return (
    <footer style={{ height: 36, background: 'var(--void)', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', padding: '0 28px', gap: 24 }}>
      {items.map(item => (
        <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-muted)' }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: item.dot }} />
          {item.label}
        </div>
      ))}
      <div style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-muted)' }}>InsightPro™ v1.2</div>
    </footer>
  );
}
